import { useContext, useEffect, useMemo, useRef, useState } from "react"
import services from "../../../services/services";
import { Urls } from "../../../services/Urls";
import { callautopopulate } from "../../../services/callautopop";
import { ToastContext } from "../../../context/ToastContext";
import _, { isArray } from 'underscore';

export const useUserGroup = () => {
    const tagRef = useRef(null);
    const [dialogVisible, setDialogVisible] = useState(false);
    const [groupName, setGroupName] = useState('');
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [users, setUsers] = useState({all_users: [], filtered_users: []});
    const [groups, setGroups] = useState([]);
    const [editingGroupId, setEditingGroupId] = useState(null);
    const { showSuccess, showError, showInfo } = useContext(ToastContext);
    const [confirmDialogVisible, setConfirmDialogVisible] = useState({user: false, group: false, message: ""});
    const [groupIdToDelete, setGroupIdToDelete] = useState(null);
    const [userIdToDelete, setUserIdToDelete] = useState(null);
    const [loader, setLoader] = useState(false);

    useEffect(() => {
        callautopopulate("users").then(res => {
            setUsers((prev) => ({...prev, all_users: _.sortBy(res?.users, "user_display_name"), filtered_users: _.sortBy(res?.users, "user_display_name")}));
            fetchGroups(res?.users);
        });
    }, []);

    const fetchGroups = async (all_users) => {
        setLoader(true)
        try {
            const response = await services.getInvokeRemote('GET',Urls('GETUSERGROUPLIST'));
            const userGroupsMap = response?.body?.data?.userGroups?.reduce(
                (acc, group) => {
                    if (!acc[group.group_name]) {
                        acc[group.group_name] = {
                            id: group.group_id,
                            group_name: group.group_name,
                            users: [],
                            expanded: false,
                        };
                    }
                    acc[group.group_name].users.push({
                        id: group.user_code,
                        name: group.user_display_name,
                        image_url: group.image_url,
                        email: group.email_id,
                    });
                    return acc;
                },
                {}
            );

            
            const userGroups = Object.values(userGroupsMap);
            setGroups(userGroups);
            const allUsers = _.uniq(_.pluck(response?.body?.data?.userGroups, "user_code"));
            setUsers((prev) => ({...prev, filtered_users: _.sortBy(_.filter(all_users || users?.all_users, item => !allUsers?.includes(item?.user_code)), "user_display_name")}));
            setLoader(false)
        } catch (error) {
            showError('Error', error.message);
            console.error(error);
            setLoader(false);
        }
    };

    const handleCreateGroup = async () => {
        if (groupName?.length === 0) {
            showInfo("info", "Group name cannot be empty");
            return;
        } else if (_.isEmpty(selectedUsers)) {
            showInfo("info", "Please select users");
            return;
        } else {
            const payload = {
                group_id: 0,
                group_name: groupName,
                user_groups: _.pluck(selectedUsers, "user_code")?.toString(),
            };
            try {
                const response = await services.getInvokeRemote(
                    'POST',
                    Urls('UPSERTUSERGROUP'),
                    payload
                );
                if (response.body.data) {
                    await fetchGroups();
                    setDialogVisible(false);
                    setGroupName('');
                    setSelectedUsers([]);
                    showSuccess('Success', 'Group created successfully!');
                } else {
                    showError('Error', response.body.errorMessage);
                }
            } catch (error) {
                if (isArray(error.response.data.error)) {
                    showError('Error', error.response.data.error[0].errorMessage);
                } else {
                    showError('Error', error.response.data.errorMessage);
                }
                console.error(error);
            }
        }
    };

    const handleSaveGroup = async () => {
        if (editingGroupId) {
            handleEditGroupSave();
        } else {
            handleCreateGroup();
        }
    };

    const handleEditGroupSave = async () => {
        const userGroups = _.pluck(selectedUsers, "user_code")?.toString();
        const payload = {
            group_id: editingGroupId,
            group_name: groupName,
            user_groups: userGroups,
        };
        try {
            const response = await services.getInvokeRemote(
                'POST',
                Urls('UPSERTUSERGROUP'),
                payload
            );
            if (response.body.data) {
                await fetchGroups();
                setDialogVisible(false);
                setGroupName('');
                setSelectedUsers([]);
                setEditingGroupId(null);
                showSuccess('Success', 'Group edited successfully!');
            } else {
                showError('Error', response.body.message);
            }
        } catch (error) {
            showError('Error', error.response.data.errorMessage);
            console.error(error);
        }
    };

    const handleDeleteGroup = async (showConfirm, groupId) => {
        if(showConfirm) {
            setConfirmDialogVisible((prev) => ({...prev, group: true, message: "Are you sure you want to delete the group?", groupId: groupId}));
            return;
        }
        const payload = {
            group_id: groupId,
        };

        try {
            const response = await services.getInvokeRemote(
                'POST',
                Urls('DELETEGROUPS'),
                payload
            );
            if (response.body.data) {
                await fetchGroups();
                showSuccess('Success', 'Group deleted successfully!');
                setConfirmDialogVisible((prev) => ({...prev, group: false}));
            } else {
                showError('Error', response.body.message);
            }
        } catch (error) {
            if (isArray(error.response.data.error)) {
                showError('Error', error.response.data.error[0].errorMessage);
            } else {
                showError('Error', error.response.data.errorMessage);
            }
            console.error(error);
        }
    };

    const handleDeleteUser = async (groupId, userId) => {
        setGroupIdToDelete(groupId);
        setUserIdToDelete(userId);
        setConfirmDialogVisible((prev) => ({...prev, user: true, message: "Are you sure you want to remove the user from the group?"}));
    };

    const handleConfirmDeleteUser = async () => {
        try {
            const payload = {
                group_id: groupIdToDelete,
                delete_user_code: userIdToDelete,
            };
            const response = await services.getInvokeRemote(
                'POST',
                Urls('DELETEUSERGROUPS'),
                payload
            );
            if (response.body.data) {
                const updatedGroups = groups.map((group) =>
                    group.id === groupIdToDelete
                        ? {
                            ...group,
                            users: group.users.filter((user) => user.id !== userIdToDelete),
                        }
                        : group
                );
                setGroups(updatedGroups);
                showSuccess('Success', 'User deleted successfully!');
                setConfirmDialogVisible((prev) => ({...prev, user: false}));
            } else {
                showError('Error', response.body.message);
            }
        } catch (error) {
            if (isArray(error.response.data.error)) {
                showError('Error', error.response.data.error[0].errorMessage);
            } else {
                showError('Error', error.response.data.errorMessage);
            }
            console.error(error);
        }
    };

    const handleToggleExpand = (groupId) => {
        setGroups(
            groups.map((group) =>
                group.id === groupId ? { ...group, expanded: !group.expanded } : group
            )
        );
    };

    const handleEditGroup = (group) => {
        setGroupName(group.group_name);
        const addedUsersUsercode = _.pluck(group?.users, "id");
        const addedUsers = _.filter(users?.all_users, item => addedUsersUsercode?.includes(item?.user_code));
        setSelectedUsers(addedUsers);
        setUsers((prev) => ({...prev, filtered_users: [...prev?.filtered_users, ...addedUsers]}))
        setEditingGroupId(group.id);
        setDialogVisible(true);
    };

    return useMemo(() => ({
        tagRef, setGroupName, selectedUsers, setSelectedUsers, editingGroupId, setEditingGroupId, dialogVisible, setDialogVisible, handleSaveGroup, groupName, groups, handleEditGroup, handleDeleteGroup, handleDeleteUser, handleToggleExpand, confirmDialogVisible, setConfirmDialogVisible, handleConfirmDeleteUser, loader, users
    }), [tagRef, setGroupName, selectedUsers, setSelectedUsers, editingGroupId, setEditingGroupId, dialogVisible, setDialogVisible, handleSaveGroup, groupName, groups, handleEditGroup, handleDeleteGroup, handleDeleteUser, handleToggleExpand, confirmDialogVisible, setConfirmDialogVisible, handleConfirmDeleteUser, loader, users])
}